import React, { useEffect } from 'react';
import { ApolloError, useQuery } from '@apollo/client';
import { useHistory, useParams } from 'react-router-dom';
import { AnalyticsEventSource } from '@atlassian/temp-analytics';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import {
  EntitlementDetails as EntitlementDetailsType,
  FetchEntitlementDetailsPpcPerfV2Query,
  FetchEntitlementDetailsPpcPerfV2QueryVariables,
} from '@css-xp/graphtypes/src/generated-types-new';
import { AppPage, ErrorComponents, useRecentlyViewed, withApollo, useFeatureToggle } from '@css-xp/base';
import { ViewPricing } from '@css-xp/base/common-sidebars';
import { ScreenEventAnalytics } from '@css-xp/ppc-common';
import {
  RecentlyViewedEntityEnum,
  routePaths,
  ENABLE_DESIGN_ITERATION_1,
  STATSIG_FEATURE_GATES,
  SUBSCRIPTION_CONSTANTS,
} from '@css-xp/ppc-constants';
import { IncludedProductsSidebar } from '@css-xp/ppc-subscription-table';
import { withErrorWrapper } from '@css-xp/ppc-wrapper';
import { getEntityName } from '@css-xp/ppc-utils';
import { EntitlementDetailsPageLoader } from '@css-xp/ppc-page-loaders';
import { fetchEntitlementDetailsPPCPerfV2 } from '../queries/fetch-entitlement-details-ppc-perf-v2';
import { EntitlementDetails } from './components';
import { SIDEBAR } from './constants/sidebar';
import {
  AddCustomerContact,
  ChangeSubscriptionPane,
  EntitlementSetInvoiceGroupPane,
  OptoutTrial,
  SetCoBillDate,
} from './sidebar-panel';
import './entitlement-page.scss';

const renderError = (error: ApolloError | undefined) => {
  if (error) {
    const GenericError = withErrorWrapper(ErrorComponents.GenericErrorPage);
    return <GenericError error={error} />;
  }
};

const allowedSidebars = {
  [SIDEBAR.SET_BILL_DATE]: withApollo(SetCoBillDate),
  [SIDEBAR.VIEW_PRICING_DETAILS]: withApollo(ViewPricing),
  [SIDEBAR.OPTOUT_TRIAL]: OptoutTrial,
  [SIDEBAR.SET_INVOICE_GROUP]: EntitlementSetInvoiceGroupPane,
  [SIDEBAR.CHANGE_SUBSCRIPTION]: ChangeSubscriptionPane,
  [SIDEBAR.ADD_CUSTOMER_CONTACT]: AddCustomerContact,
  [SUBSCRIPTION_CONSTANTS.SUBSCRIPTION_PAGE_SIDEBAR.INCLUDED_PRODUCTS]: IncludedProductsSidebar,
};

export const EntitlementPage = () => {
  const { entitlementId, txaId } = useParams<{ entitlementId: string; txaId: string }>();
  const history = useHistory();
  const { updateRecentlyViewed } = useRecentlyViewed();
  const [isEnabled] = useFeatureToggle();
  const isDesignIteration1Enabled = isEnabled(ENABLE_DESIGN_ITERATION_1);

  // Optimized Query
  const {
    loading: fetchEntitlementDetailsPPCPerfV2QueryLoading,
    error: fetchEntitlementDetailsPPCPerfV2QueryError,
    data: fetchEntitlementDetailsPPCPerfV2QueryData,
    refetch: fetchEntitlementDetailsPPCPerfV2QueryRefetch,
  } = useQuery<FetchEntitlementDetailsPpcPerfV2Query, FetchEntitlementDetailsPpcPerfV2QueryVariables>(
    fetchEntitlementDetailsPPCPerfV2,
    {
      variables: {
        id: entitlementId,
        transactionAccountId: txaId,
        fetchIsExternallyBilled: true,
      },
      context: {
        headers: {
          'x-operation-name': 'fetchEntitlementDetails', // this header will reach the server
        },
      },
    }
  );

  // Redirect to not found page if entitlement ID does not belong to the TXA
  const notFoundText = 'Cannot return null for non-nullable field EntitlementDetails.id.';
  if (fetchEntitlementDetailsPPCPerfV2QueryError?.message === notFoundText) {
    history.push(routePaths.notFound);
  }

  const entitlementDetails = {
    ...fetchEntitlementDetailsPPCPerfV2QueryData?.entitlementDetails,
  } as EntitlementDetailsType;

  const hasEntitlementDetailError =
    !entitlementDetails.id && fetchEntitlementDetailsPPCPerfV2QueryError
      ? fetchEntitlementDetailsPPCPerfV2QueryError
      : undefined;

  const isLoading = fetchEntitlementDetailsPPCPerfV2QueryLoading;
  /**
   * Use Recently Feature starts
   */
  useEffect(() => {
    if (hasEntitlementDetailError || isLoading || !fetchEntitlementDetailsPPCPerfV2QueryData) {
      return;
    }
    const { entitlementDetails } = fetchEntitlementDetailsPPCPerfV2QueryData;
    updateRecentlyViewed({
      entityName: getEntityName(RecentlyViewedEntityEnum.SUBSCRIPTION, txaId),
      metadata: {
        id: entitlementDetails?.id,
        productName: entitlementDetails?.productName,
        entitlementNumber: entitlementDetails?.entitlementNumber,
        productId: entitlementDetails?.productId,
        logoId: entitlementDetails?.logoId,
      },
    });
  }, [fetchEntitlementDetailsPPCPerfV2QueryData, hasEntitlementDetailError, isLoading]);
  /**
   * Use Recently Feature ends
   */

  const renderLoader = () => {
    if (isDesignIteration1Enabled) {
      return <EntitlementDetailsPageLoader />;
    }
    return <LoadingIndicator />;
  };

  return (
    <AppPage allowedSidebars={allowedSidebars} className="app-page-v2">
      <ScreenEventAnalytics
        event={{
          name: AnalyticsEventSource.PARTNER_ENTITLEMENT_SCREEN,
          attributes: {
            entitlementId: entitlementDetails?.id,
          },
        }}
      />

      <div className="entitlement-page" data-testid="entitlement-page-app">
        {isLoading
          ? renderLoader()
          : renderError(hasEntitlementDetailError) ?? (
              <EntitlementDetails
                entitlement={entitlementDetails}
                refetch={fetchEntitlementDetailsPPCPerfV2QueryRefetch}
                transactionAccountId={txaId}
              />
            )}
      </div>
    </AppPage>
  );
};
